import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchSkillsAndTools } from '../../ducks/SkillsAndTools.duck';
import { fetchOffPlatformTestimonials, fetchOffPlatformReviews } from '../../util/api';

// Freelancer cards are shown only when the query returns at least this many
// results; below that the page falls back to the no-results layout.
export const MIN_RESULTS_TO_SHOW = 3;
export const MAX_RESULTS = 5;
const MIN_PROFILE_POINTS = 60;

// ================ Action types ================ //

export const QUERY_FREELANCERS_REQUEST = 'app/SkillSeoPage/QUERY_FREELANCERS_REQUEST';
export const QUERY_FREELANCERS_SUCCESS = 'app/SkillSeoPage/QUERY_FREELANCERS_SUCCESS';
export const QUERY_FREELANCERS_ERROR = 'app/SkillSeoPage/QUERY_FREELANCERS_ERROR';

export const QUERY_CARD_DATA_SUCCESS = 'app/SkillSeoPage/QUERY_CARD_DATA_SUCCESS';

// ================ Reducer ================ //

const initialState = {
  queriedSkillKey: null,
  currentPageResultIds: [],
  queryInProgress: false,
  queryFreelancersError: null,
  reviews: [],
  offPlatformReviews: [],
  offPlatformTestimonials: [],
};

const reducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case QUERY_FREELANCERS_REQUEST:
      return {
        ...initialState,
        queriedSkillKey: payload.skillKey,
        queryInProgress: true,
      };
    case QUERY_FREELANCERS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: payload.listings.map(l => l.id),
        queryInProgress: false,
      };
    case QUERY_FREELANCERS_ERROR:
      return { ...state, queryInProgress: false, queryFreelancersError: payload };

    case QUERY_CARD_DATA_SUCCESS:
      return {
        ...state,
        reviews: payload.reviews,
        offPlatformReviews: payload.offPlatformReviews,
        offPlatformTestimonials: payload.offPlatformTestimonials,
      };
    default:
      return state;
  }
};

export default reducer;

// ================ Action creators ================ //

export const queryFreelancersRequest = skillKey => ({
  type: QUERY_FREELANCERS_REQUEST,
  payload: { skillKey },
});

export const queryFreelancersSuccess = listings => ({
  type: QUERY_FREELANCERS_SUCCESS,
  payload: { listings },
});

export const queryFreelancersError = e => ({
  type: QUERY_FREELANCERS_ERROR,
  error: true,
  payload: e,
});

export const queryCardDataSuccess = payload => ({
  type: QUERY_CARD_DATA_SUCCESS,
  payload,
});

// ================ Thunks ================ //

// Queries the freelancer profile listings for one skill page: freelancers who
// have the page's skill in their experience keys (mirrored onto the profile
// listing as publicData.authorSkillsKeys), profilePoints of 60+, best first.
export const queryFreelancers = skillKey => (dispatch, getState, sdk) => {
  dispatch(queryFreelancersRequest(skillKey));

  // Skill/tool options are needed to render the experience pills on the cards.
  const { skillOptions, toolOptions } = getState().SkillsAndTools;
  const fetchSkillsAndToolsIfNeeded =
    skillOptions.length === 0 || toolOptions.length === 0
      ? dispatch(fetchSkillsAndTools())
      : Promise.resolve();

  const searchParams = {
    pub_listingType: 'profile',
    pub_authorRole: 'has_any:freelancer,light-entrepreneur',
    pub_authorSkillsKeys: `has_any:${skillKey}`,
    meta_showAtMarketPlace: true,
    // Open-ended range: profilePoints >= MIN_PROFILE_POINTS
    meta_profilePoints: `${MIN_PROFILE_POINTS},`,
    sort: 'meta_profilePoints',
    perPage: MAX_RESULTS,
    include: ['author', 'author.profileImage'],
    'fields.listing': ['title', 'publicData', 'metadata'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };

  return sdk.listings
    .query(searchParams)
    .then(response => {
      // Flex silently ignores filter/sort params for extended-data attributes
      // that have no search schema. The meta_profilePoints constraint above
      // requires one (flex-cli: search set --key profilePoints --type long
      // --scope metadata), so the same constraint is re-applied here
      // defensively — a no-op when the schema is in place.
      const listings = response.data.data
        .filter(l => (l.attributes.metadata?.profilePoints || 0) >= MIN_PROFILE_POINTS)
        .sort(
          (a, b) =>
            (b.attributes.metadata?.profilePoints || 0) -
            (a.attributes.metadata?.profilePoints || 0)
        );

      dispatch(addMarketplaceEntities(response));
      dispatch(queryFreelancersSuccess(listings));
      return listings;
    })
    .then(async listings => {
      // The cards are rendered only when there are enough results — skip the
      // per-author fetches otherwise.
      if (listings.length < MIN_RESULTS_TO_SHOW) {
        return listings;
      }

      const authorIds = [
        ...new Set(listings.map(l => l.relationships?.author?.data?.id?.uuid).filter(Boolean)),
      ];

      const reviewsPromises = authorIds.map(id =>
        sdk.reviews
          .query({ subject_id: id, state: 'public', include: ['subject'] })
          .then(res => res.data.data)
          .catch(() => [])
      );
      const offPlatformReviewsPromises = authorIds.map(id =>
        fetchOffPlatformReviews(id)
          .then(res => res.items || [])
          .catch(() => [])
      );
      const offPlatformTestimonialsPromises = authorIds.map(id =>
        fetchOffPlatformTestimonials(id)
          .then(res => res.items || [])
          .catch(() => [])
      );

      const [reviews, offPlatformReviews, offPlatformTestimonials] = await Promise.all([
        Promise.all(reviewsPromises),
        Promise.all(offPlatformReviewsPromises),
        Promise.all(offPlatformTestimonialsPromises),
      ]);

      dispatch(
        queryCardDataSuccess({
          reviews: reviews.flat(),
          offPlatformReviews: offPlatformReviews.flat(),
          offPlatformTestimonials: offPlatformTestimonials.flat(),
        })
      );

      await fetchSkillsAndToolsIfNeeded;

      return listings;
    })
    .catch(e => {
      dispatch(queryFreelancersError(storableError(e)));
    });
};

export const loadData = content => queryFreelancers(content.skillKey);
